<template>
    <div>
        <div class="kgr-row">
            <h3 class="kgr-headline kgr-headline--h3">
                {{ $t('SEPA-Lastschriften') }}
            </h3>
        </div>
        <div class="kgr-row">
            <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <kgr-checkbox
                        v-model="filter.showInactive"
                        :label="$t('Inaktive SEPA-Lastschriften anzeigen')"
                    />
                </div>
                <div class="kgr-grid__cell">
                    <button
                        class="kgr-button kgr-button--accent kgr-float kgr-float--right"
                        @click="openCreateDialog()"
                    >
                        {{ $t('Neue SEPA-Lastschrift anlegen') }}
                    </button>
                </div>
            </div>
        </div>
        <table class="kgr-table kgr-table--block kgr-table--striped printable">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Bankkonto') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Mandatsreferenz') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Standardkonto') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Startdatum des Standardkontos') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-hidden-print"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td
                        colspan="5"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="filteredSepaDebits.length === 0">
                    <td
                        colspan="5"
                        style="text-align: center;"
                    >
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="sepaDebit in filteredSepaDebits"
                        :key="sepaDebit.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            {{ sepaDebit.memberBankAccount.owner }} ({{ sepaDebit.memberBankAccount.iban }})
                        </td>
                        <td class="kgr-table__cell">
                            {{ sepaDebit.mandateReference.number }}
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-icon
                                v-if="sepaDebit.defaultStartingAt !== null"
                                :style="sepaDebit.isCurrentDefault ? 'color: green;' : ''"
                                icon-class="check"
                                disabled
                            />
                        </td>
                        <td class="kgr-table__cell">
                            <template v-if="null !== sepaDebit.defaultStartingAt">
                                {{ $t('gültig ab') }} {{ formatDate(sepaDebit.defaultStartingAt) }}
                            </template>
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions kgr-hidden-print">
                            <button
                                class="kgr-table__action"
                                :disabled="sepaDebit.defaultStartingAt !== null"
                                @click="openEditDialog(sepaDebit)"
                            >
                                <kgr-icon
                                    icon-class="edit"
                                    :disabled="sepaDebit.defaultStartingAt !== null"
                                />
                            </button>
                            <button
                                class="kgr-table__action"
                                :disabled="!sepaDebit.isDeletable"
                                @click="deleteSepaDebit(sepaDebit.id)"
                            >
                                <kgr-icon
                                    icon-class="delete"
                                    :disabled="!sepaDebit.isDeletable"
                                />
                            </button>
                            <button
                                class="kgr-table__action"
                                :disabled="sepaDebit.isDeletable || sepaDebit.isArchived"
                                @click="archiveMemberSepaDebit(sepaDebit.id)"
                            >
                                <kgr-icon
                                    icon-class="archive"
                                    :disabled="sepaDebit.isDeletable || sepaDebit.isArchived"
                                />
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <kgr-lightbox
            v-if="newSepaDebit"
            :value="showDialog"
            :headline="$t('Neue SEPA-Lastschrift anlegen')"
            animated
            @post-input="closeModal"
        >
            <div>
                <kgr-form-row
                    :label="$t('Bankkonto')"
                    :error="error"
                    field-name="memberBankAccountId"
                >
                    <kgr-generic-select
                        v-model="newSepaDebit.bankAccount"
                        :options="memberBankAccounts"
                        display="display"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Mandatsreferenz')"
                    :error="error"
                    field-name="mandateReferenceId"
                >
                    <kgr-generic-select
                        v-model="newSepaDebit.mandateReference"
                        :options="memberMandateReferences"
                        display="number"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Standard-Konto für Gebühren')"
                    :error="error"
                    field-name="withDate"
                >
                    <kgr-checkbox v-model="newSepaDebit.withDate" />
                </kgr-form-row>
                <kgr-form-row
                    v-if="newSepaDebit.withDate"
                    :label="$t('Gültig ab')"
                    :error="error"
                    field-name="defaultStartingAt"
                >
                    <kgr-date-picker
                        v-model="newSepaDebit.defaultStartingAt"
                    />
                </kgr-form-row>
                <hr class="kgr-separator--light" />
                <div class="kgr-row">
                    <button
                        class="kgr-button"
                        @click="closeModal()"
                    >
                        {{ $t('cancel') }}
                    </button>
                    <button
                        class="kgr-button kgr-button--accent"
                        @click="createSepaDebit()"
                    >
                        {{ $t('save') }}
                    </button>
                </div>
            </div>
        </kgr-lightbox>
        <kgr-lightbox
            v-if="modalSepaDebit"
            :value="showEditDialog"
            :headline="$t('Lastschrift bearbeiten')"
            animated
            @post-input="closeEditModal"
        >
            <div>
                <kgr-form-row
                    :label="$t('Bankkonto')"
                    :error="error"
                    field-name="memberBankAccountId"
                >
                    <kgr-text-input
                        v-model="modalSepaDebit.bankAccount"
                        disabled
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Mandatsreferenz')"
                    :error="error"
                    field-name="mandateReferenceId"
                >
                    <kgr-text-input
                        v-model="modalSepaDebit.mandateReference"
                        disabled
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Standard-Konto für Gebühren')"
                    :error="error"
                    field-name="withDate"
                >
                    <kgr-checkbox v-model="modalSepaDebit.withDate" />
                </kgr-form-row>
                <kgr-form-row
                    v-if="modalSepaDebit.withDate"
                    :label="$t('Gültig ab')"
                    :error="error"
                    field-name="defaultStartingAt"
                >
                    <kgr-date-picker
                        v-model="modalSepaDebit.defaultStartingAt"
                    />
                </kgr-form-row>
                <hr class="kgr-separator--light" />
                <div class="kgr-row">
                    <button
                        class="kgr-button"
                        @click="closeEditModal()"
                    >
                        {{ $t('cancel') }}
                    </button>
                    <button
                        class="kgr-button kgr-button--accent"
                        :disabled="!modalSepaDebit.withDate"
                        @click="updateSepaDebit(modalSepaDebit)"
                    >
                        {{ $t('save') }}
                    </button>
                </div>
            </div>
        </kgr-lightbox>
        <kgr-default-payment-method-assign
            v-if="showDefaultPaymentDialog"
            :show="showDefaultPaymentDialog"
            :member-sepa-debit="defaultPaymentAssignment.memberSepaDebit"
            :member-club-fees="defaultPaymentAssignment.memberClubFees"
            :member-id="id"
            @close="onAssignmentDialogClosed()"
            @assigned="onAssignmentDialogAssigned()"
        />
    </div>
</template>

<script>
import { format, parse, parseISO } from '@legacy/js/date/date-fns.js';
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import DatePicker from '../../../components/common/form/picker/DatePicker.vue';
import DefaultPaymentMethodAssign from '../../../components/members/accounting/DefaultPaymentMethodAssign.vue';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import Lightbox from '../../../components/common/modal/Lightbox.vue';
import Loading from '../../../components/common/Loading.vue';
import MemberClubFee from '../../../js/dto/MemberClubFee.js';
import TextInput from '../../../components/common/form/input/TextInput.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-form-row': FormRow,
        'kgr-lightbox': Lightbox,
        'kgr-checkbox': Checkbox,
        'kgr-icon': Icon,
        'kgr-generic-select': GenericSelect,
        'kgr-default-payment-method-assign': DefaultPaymentMethodAssign,
        'kgr-loader': Loading,
        'kgr-date-picker': DatePicker,
        'kgr-text-input': TextInput,
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: false,
            initialLoading: true,
            defaultPaymentAssignment: null,
            showDefaultPaymentDialog: false,
            showDialog: false,
            showEditDialog: false,
            modalSepaDebit: null,
            sepaDebits: [],
            memberBankAccounts: [],
            memberMandateReferences: [],
            newSepaDebit: null,
            filter: {
                showInactive: false,
            },
        }
    },
    computed: {
        occupiedDates() {
            return this.sepaDebits.filter(sepaDebit => sepaDebit.defaultStartingAt !== null).map(sepaDebit => sepaDebit.defaultStartingAt);
        },
        filterText() {
            return this.filter.showInactive ? this.$t('Alle SEPA-Lastschriften werden angezeigt') : this.$t('Archivierte SEPA-Lastschriften sind ausgeblendet');
        },
        filteredSepaDebits() {
            if (!this.filter.showInactive) {
                return this.sepaDebits.filter(filteredSepaDebit => !filteredSepaDebit.isArchived);
            }
            return this.sepaDebits;
        }
    },
    created() {
        this.loadSepaDebits();
    },
    methods: {
        formatDate(dateOrNull) {
            if(null === dateOrNull) {
                return '-';
            }
            return format(parseISO(dateOrNull), 'dd.MM.yyyy');
        },
        loadSepaDebits() {
            this.loading = true;
            return axios
                .get('/club/members/' + this.id + '/member-sepa-debits')
                .then(response => {
                    this.sepaDebits = response.data.data;
                    this.loading = false;
                    this.initialLoading = false;
                });
        },
        createSepaDebit() {
            let formattedDate = null
            if(this.newSepaDebit.withDate && this.newSepaDebit.defaultStartingAt !== null) {
                const creditTransferStartingAt= parse(this.newSepaDebit.defaultStartingAt, 'dd.MM.yyyy', new Date());
                formattedDate = format(creditTransferStartingAt, 'yyyy-MM-dd');

            }
            let payload = {
                'memberBankAccountId': this.newSepaDebit.bankAccount?.id,
                'mandateReferenceId': this.newSepaDebit.mandateReference?.id,
                'defaultStartingAt': formattedDate
            };
            return axios
                .post('/club/members/' + this.id + '/member-sepa-debits', payload)
                .then(response => {
                    var showDefaultAssignmentDialog = this.newSepaDebit.withDate && this.newSepaDebit.defaultStartingAt !== null;
                    this.closeModal();
                    let promise = this.loadSepaDebits();
                    if(showDefaultAssignmentDialog) {
                        let location = response.headers.location;
                        var memberSepaDebitId =  location.substring(location.lastIndexOf('/')+1);
                        promise.then(() => {
                            this.enableDefaultAssignmentDialog(memberSepaDebitId)
                        });
                    }
                    this.$emit('save');
                })
                .catch((error) => this.error = Error.fromResponse(error.response));
        },
        enableDefaultAssignmentDialog(memberSepaDebitId) {
            this.loading = true;
            let memberSepaDebit = this.sepaDebits.find(sepaDebit => sepaDebit.id === memberSepaDebitId);
            axios
                .get('/club/invoice-recipients/' + this.id + '/member-club-fees')
                .then(response => {
                    this.memberClubFeesForDefaultPayment = response.data.data;
                    this.defaultPaymentAssignment = {
                        memberClubFees: response.data.data.map(memberClubFee => MemberClubFee.fromData(memberClubFee)),
                        memberSepaDebit: memberSepaDebit
                    };
                    this.showDefaultPaymentDialog = true;
                    this.loading = false;
                    this.initialLoading = false;
                })
                .catch(() => this.loading = false);
        },
        onAssignmentDialogAssigned() {
            this.defaultPaymentAssignment.memberSepaDebit.isDeletable = false;
        },
        updateSepaDebit(memberSepaDebit) {
            let formattedDate = null
            if(memberSepaDebit.withDate && memberSepaDebit.defaultStartingAt !== null) {
                const creditTransferStartingAt= parse(memberSepaDebit.defaultStartingAt, 'dd.MM.yyyy', new Date());
                formattedDate = format(creditTransferStartingAt, 'yyyy-MM-dd');

            }
            axios
                .patch('/club/member-sepa-debits/' + memberSepaDebit.id + '/default', {
                    'defaultStartingAt': formattedDate
                })
                .then(() => {
                    let promise = this.loadSepaDebits();
                    if(memberSepaDebit.withDate && null !== memberSepaDebit.defaultStartingAt) {
                        promise.then(() => {
                            this.enableDefaultAssignmentDialog(memberSepaDebit.id)
                        });
                    }
                    this.$emit('save');
                    this.closeEditModal();
                })
                .catch((error) => this.error = Error.fromResponse(error.response));
        },
        archiveMemberSepaDebit(memberSepaDebitId) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst Du die SEPA-Lastschrift wirklich archivieren?'), () => {
                axios
                    .put('/club/member-sepa-debits/' + memberSepaDebitId + '/archive')
                    .then(() => {
                        this.loadSepaDebits();
                        this.$emit('save');
                    })
                    .catch(() => this.$notify.error('error.loadingData'));
            });
        },
        deleteSepaDebit(id) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst Du die SEPA-Lastschrift wirklich löschen?'), () => {
                axios
                    .delete('/club/member-sepa-debits/' + id)
                    .then(() => {
                        this.loadSepaDebits();
                        this.$emit('save');
                    })
                    .catch(() => this.$notify.error('error.loadingData'));
            });
        },
        loadMemberBankAccounts() {
            axios
                .get('/club/members/' + this.id + '/member-bank-accounts')
                .then(response => {
                    this.memberBankAccounts = response.data.data.map((account) => {
                        return {
                            id: account.id,
                            display: account.owner + ' (' + account.iban + ' ' + account.bic + ')'
                        }
                    })
                });

        },
        loadMemberMandateReferences() {
            axios
                .get('/club/members/' + this.id + '/mandate-references')
                .then(response => this.memberMandateReferences = response.data.data);
        },
        initNew() {
            this.loadMemberMandateReferences();
            this.loadMemberBankAccounts();
            this.newSepaDebit = {
                bankAccount: '',
                mandateReference: '',
                withDate: false,
                defaultStartingAt: format(new Date(), 'dd.MM.yyyy'),
            };
        },
        onAssignmentDialogClosed() {
            this.showDefaultPaymentDialog = false;
            this.defaultPaymentAssignment = null;
        },
        openCreateDialog() {
            this.error = new Error();
            this.initNew();
            this.showDialog = true;
        },
        closeModal() {
            this.showDialog = false;
        },
        openEditDialog(sepaDebit) {
            this.error = new Error();
            let defaultStartingAt = sepaDebit.defaultStartingAt ? parse(sepaDebit.defaultStartingAt, 'yyyy-MM-dd', new Date()) : new Date();
            this.modalSepaDebit = {
                id: sepaDebit.id,
                bankAccount: `${sepaDebit.memberBankAccount.owner} (${sepaDebit.memberBankAccount.iban})`,
                mandateReference: sepaDebit.mandateReference.number,
                withDate: false,
                defaultStartingAt: format(defaultStartingAt, 'dd.MM.yyyy'),
            };
            this.showEditDialog = true;
        },
        closeEditModal() {
            this.showEditDialog = false;
            this.modalSepaDebit = null;
        },
    },
}
</script>
